import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useClickOutside } from '../../../hooks/useClickOutside';
import { buildShareHref, ShareChannelId } from '../../../utils/buildConsultationShareLinks';
import { IconProps, Tooltip, IconButton } from '../../../shared/ui';
import { EmailIcon } from '../../icons/email';
import { MaxIcon } from '../../icons/max';
import { ShareIcon } from '../../icons/share';
import { TgIcon } from '../../icons/tg';
import { VkIcon } from '../../icons/vk';
import { WaIcon } from '../../icons/wa';

const SHARE_CHANNELS: { id: ShareChannelId; label: string; Icon: FC<IconProps> }[] = [
  { id: 'telegram', label: 'Telegram', Icon: TgIcon },
  { id: 'whatsapp', label: 'WhatsApp', Icon: WaIcon },
  { id: 'vk', label: 'ВКонтакте', Icon: VkIcon },
  { id: 'max', label: 'MAX', Icon: MaxIcon },
  { id: 'email', label: 'Электронная почта', Icon: EmailIcon },
];

type ConsultationShareProps = {
  /** Готовый текст приглашения — тот же, что уходит в буфер по кнопке копирования. */
  inviteText: string;
  link: string;
};

export const ConsultationShare: FC<ConsultationShareProps> = ({ inviteText, link }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  useClickOutside(containerRef, close);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, close]);

  // Карточки на мобильном лежат в grid-cols-2 (consultation-table/index.tsx) — окно
  // выпадающего меню там шире самой карточки. Блокируем скролл страницы под шторкой,
  // как в остальных полноэкранных на мобильном оверлеях репозитория (modal-container).
  useEffect(() => {
    if (!isOpen || !window.matchMedia('(max-width: 639px)').matches) return;

    document.body.classList.add('overflow-hidden');

    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  const renderChannelRow = (rowClassName: string, iconSize: number) =>
    SHARE_CHANNELS.map(({ id, label, Icon }) => {
      const href = buildShareHref(id, { text: inviteText, link });
      const isMailto = href.startsWith('mailto:');

      return (
        <a
          key={id}
          role="menuitem"
          href={href}
          target={isMailto ? undefined : '_blank'}
          rel="noopener noreferrer"
          onClick={close}
          className={rowClassName}
        >
          <Icon width={iconSize} height={iconSize} needHover={false} />
          {label}
        </a>
      );
    });

  const mobileRowClassName =
    'flex items-center gap-3 rounded-control p-3 text-body-l text-ink transition-colors active:bg-surface-muted';
  const desktopRowClassName =
    'flex items-center gap-2.5 rounded-control p-1.5 text-ui text-ink transition-colors hover:bg-surface-muted';

  return (
    <div ref={containerRef} className="relative">
      <Tooltip text="Поделиться ссылкой на встречу">
        <IconButton
          label="Поделиться ссылкой на встречу"
          className="group"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <ShareIcon width={16} height={16} className="transition-colors text-ink group-hover:text-primary" />
        </IconButton>
      </Tooltip>

      {isOpen && (
        <>
          {/* Мобильная версия — шторка снизу поверх затемнения, крупные зоны нажатия. */}
          <div className="sm:hidden">
            <div className="fixed inset-0 z-modal bg-ink/60" onClick={close} aria-hidden="true" />
            <div
              role="menu"
              className="fixed inset-x-0 bottom-0 z-modal flex flex-col gap-1 rounded-t-card bg-white p-3 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-raised"
            >
              <div className="mx-auto mb-1 h-1 w-10 rounded-full bg-line" aria-hidden="true" />
              {renderChannelRow(mobileRowClassName, 40)}
            </div>
          </div>

          {/* Десктоп/планшет — компактное выпадающее меню у кнопки. */}
          <div
            role="menu"
            className="hidden sm:flex absolute right-0 top-full z-20 mt-2 w-[230px] flex-col gap-1 rounded-control border border-line-strong bg-white p-2 shadow-raised"
          >
            {renderChannelRow(desktopRowClassName, 32)}
          </div>
        </>
      )}
    </div>
  );
};
